import { FilterHorizontalIcon, Search01Icon } from '@hugeicons/core-free-icons';
import type { ReactNode } from 'react';
import { Button } from '../../components/ui/Button';
import { CustomSelect, type SelectOption } from '../../components/ui/CustomSelect';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { Input } from '../../components/ui/Input';
import { OperationalTableCard } from '../../components/ui/operational-table-card';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

export type TicketStatusFilter = 'all' | 'open' | 'in_progress' | 'closed';
export type TicketPriorityFilter = 'all' | 'low' | 'normal' | 'high';

const statusOptions: ReadonlyArray<SelectOption<TicketStatusFilter>> = [
  { label: 'All statuses', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Closed', value: 'closed' },
];

const priorityOptions: ReadonlyArray<SelectOption<TicketPriorityFilter>> = [
  { label: 'All priorities', value: 'all' },
  { label: 'High priority', value: 'high' },
  { label: 'Normal priority', value: 'normal' },
  { label: 'Low priority', value: 'low' },
];

export function TicketDirectoryToolbar({
  customer,
  onPriorityChange,
  onQueryChange,
  onStatusChange,
  priority,
  query,
  searchPlaceholder = 'Search subject or customer',
  status,
}: {
  customer?: {
    onChange: (value: string) => void;
    options: ReadonlyArray<SelectOption>;
    value: string;
  };
  onPriorityChange: (value: TicketPriorityFilter) => void;
  onQueryChange: (value: string) => void;
  onStatusChange: (value: TicketStatusFilter) => void;
  priority: TicketPriorityFilter;
  query: string;
  searchPlaceholder?: string;
  status: TicketStatusFilter;
}) {
  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <HugeIcon className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" icon={Search01Icon} size={15} />
        <Input
          aria-label="Search support tickets"
          className="w-full rounded-full pl-9"
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={searchPlaceholder}
          value={query}
        />
      </div>
      <Popover>
        <PopoverTrigger render={<Button aria-label="Filter support tickets" className="shrink-0 rounded-full" size="icon" type="button" variant="outline" />}>
          <HugeIcon icon={FilterHorizontalIcon} size={18} />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 gap-3 rounded-2xl p-3">
          {customer ? (
            <CustomSelect ariaLabel="Filter by customer" fullWidth label="Customer" onChange={customer.onChange} options={customer.options} value={customer.value} />
          ) : null}
          <CustomSelect ariaLabel="Filter by status" fullWidth label="Status" onChange={onStatusChange} options={statusOptions} value={status} />
          <CustomSelect ariaLabel="Filter by priority" fullWidth label="Priority" onChange={onPriorityChange} options={priorityOptions} value={priority} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function TicketDirectoryCard({
  children,
  className,
  compact,
  information,
  informationLabel,
  toolbar,
}: {
  children: ReactNode;
  className?: string;
  compact: boolean;
  information: ReactNode;
  informationLabel: string;
  toolbar: ReactNode;
}) {
  return (
    <OperationalTableCard
      action={toolbar}
      actionClassName={compact
        ? 'col-start-1 row-start-2 w-full justify-self-stretch'
        : 'col-start-1 row-start-2 w-full justify-self-stretch lg:col-start-2 lg:row-start-1 lg:w-auto lg:min-w-80 lg:justify-self-end'}
      className={className}
      headerClassName={compact
        ? 'grid-cols-1 grid-rows-[auto_auto] py-3!'
        : 'grid-cols-1 grid-rows-[auto_auto] py-3! lg:grid-cols-[minmax(0,1fr)_minmax(20rem,auto)] lg:grid-rows-1 lg:py-0!'}
      information={information}
      informationLabel={informationLabel}
      title="Support tickets"
    >
      {children}
    </OperationalTableCard>
  );
}
